<template>
    <div ref="componentRef" class="min-h-screen bg-white">
        <!-- Search Bar Container - Positioned to align with navbar -->
        <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
            <div class="flex justify-end mb-2">
                <div class="flex items-center border-b-2 border-gray-300 max-w-sm w-full">
                    <input type="text" v-model="searchQuery" placeholder="Search authors..."
                        @keydown.enter="handleSearch" @input="handleSearch"
                        class="flex-1 bg-transparent text-primary placeholder-gray-400 border-none outline-none text-sm font-body mr-3 text-left" />
                    <button v-if="searchQuery.trim()" @click="clearSearch"
                        class="p-1.5 text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0"
                        aria-label="Clear search">
                        <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <button @click="handleSearch"
                        class="p-1.5 bg-accent text-white hover:bg-green-700 transition-colors flex-shrink-0"
                        aria-label="Search">
                        <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Main Content Container -->
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="text-center mb-2.5">
                <h1 class="font-header text-[3rem] font-bold text-primary mb-1.5">Our Authors</h1>
                <div class="font-body text-[1.125rem] leading-[1.75] text-gray-700 max-w-4xl mx-auto content-page-description"
                    v-html="pageDescription || 'Discover the talented writers behind our collection of books. From bestselling novelists to emerging voices, explore the authors who bring stories to life.'">
                </div>
            </div>

            <div v-if="loading" class="flex justify-center items-center min-h-[400px]">
                <Loader size="large" />
            </div>

            <div v-else-if="filteredAuthors.length > 0">
                <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
                    <AuthorCard v-for="author in filteredAuthors" :key="author.name" :author="author" />
                </div>
            </div>

            <div v-else class="text-center py-1">
                <p class="font-body text-[1.125rem] leading-[1.75] text-gray-600">No authors found.</p>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import AuthorCard from './AuthorCard.vue'
import Loader from './Loader.vue'
import { createAxiosInstance } from '../utils/axios.js'
import { translationService } from '../services/translation'
import { publicService } from '../services/public'
import { useLoadingState } from '../composables/useLoadingState'
import { useDataTranslation } from '../composables/useDataTranslation'

const api = createAxiosInstance()
api.interceptors.request.use(config => {
    const language = translationService.getLanguage()
    config.headers['Accept-Language'] = language
    config.headers['X-Language'] = language
    return config
})

const authors = ref([])
const searchQuery = ref('')
const { loading, setLoading } = useLoadingState(true)
const pageDescription = ref('')
const componentRef = ref(null)

const { translatePage, handleLanguageChange: handleTranslation } = useDataTranslation(componentRef, loading, '.min-h-screen.bg-white', setLoading)

const handleLanguageChange = async () => {
    await loadAuthors()
    await loadPageDescription()
    await handleTranslation()
}

const filteredAuthors = computed(() => {
    if (!searchQuery.value.trim()) {
        return authors.value
    }
    const query = searchQuery.value.toLowerCase()
    return authors.value.filter(author =>
        author.name.toLowerCase().includes(query) ||
        (author.location && author.location.toLowerCase().includes(query))
    )
})

const handleSearch = () => {
    // Search is handled reactively by filteredAuthors computed property
    // This function exists for the button click handler
}

const clearSearch = () => {
    searchQuery.value = ''
}

const loadAuthors = async () => {
    setLoading(true)
    try {
        const authorsResponse = await api.get('/api/authors', {
            params: { limit: 1000 }
        })

        const authorsData = authorsResponse.data.pagination
            ? authorsResponse.data.authors
            : (Array.isArray(authorsResponse.data) ? authorsResponse.data : [])

        authors.value = authorsData.map(author => ({
            id: author.id,
            name: author.name,
            bookCount: author.book_count || 0,
            image: author.image_url || null,
            location: author.location || null,
            description: author.description || null,
            imageCropDimensions: author.image_crop_dimensions || null
        })).sort((a, b) => a.name.localeCompare(b.name))
    } catch (error) {
        console.error('Error loading authors:', error)
        authors.value = []
    } finally {
        setLoading(false)
    }
}

const loadPageDescription = async () => {
    try {
        const page = await publicService.getPage('Authors')
        if (page && page.description) {
            pageDescription.value = page.description
        }
    } catch (error) {
        console.error('Failed to load page description:', error)
    }
}

onMounted(async () => {
    window.addEventListener('language-changed', handleLanguageChange)
    await loadAuthors()
    await loadPageDescription()
    await translatePage()
})

onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
})
</script>

<style scoped></style>
